import type { ReactElement, ReactNode } from "react";
import type { KurooClient } from "@kuroo/sdk";

export type SettingsMap = Record<string, unknown>;

export type SettingsSection =
  | "general"
  | "activation"
  | "company"
  | "payments"
  | "appointments"
  | "events"
  | "notifications"
  | "email"
  | "sms"
  | "employee"
  | "customer"
  | "administrator";

export type SettingsPageProps = {
  client: KurooClient;
  expire: () => void;
  navigate: (path: string) => void;
  canManageIntegrations: boolean;
  canManageRolePolicies: boolean;
  environmentLabel: string;
};

export type SaveSettings = (values: Record<string, unknown>, successMessage: string) => Promise<void>;

export type SettingsEditorProps = {
  settings: SettingsMap;
  setSetting: (key: string, value: unknown) => void;
  save: SaveSettings;
  saving: boolean;
  navigate: (path: string) => void;
  canManageIntegrations: boolean;
  canManageRolePolicies: boolean;
};

export const sectionMeta: Record<SettingsSection, { title: string; description: string }> = {
  general: { title: "General", description: "Configurá el comportamiento predeterminado de tu agenda y del proceso de reserva." },
  activation: { title: "Activación", description: "Estado de tu cuenta y acceso a las funciones de Kuroo CRM." },
  company: { title: "Empresa", description: "Datos públicos e identidad visual que verá tu clientela." },
  payments: { title: "Pagos", description: "Definí cómo se cobran las reservas y cómo se muestran los importes." },
  appointments: { title: "Citas", description: "Reglas y permisos para las citas individuales." },
  events: { title: "Eventos", description: "Preferencias para la publicación, inscripción y capacidad de eventos." },
  notifications: { title: "General", description: "Preferencias globales de comunicación para clientes y profesionales." },
  email: { title: "Correo electrónico", description: "Configurá qué mensajes salen por correo y desde qué dirección." },
  sms: { title: "SMS", description: "Configurá los avisos SMS y el remitente utilizado por la integración." },
  employee: { title: "Empleado", description: "Elegí qué acciones pueden realizar los profesionales desde su agenda." },
  customer: { title: "Cliente", description: "Controlá qué gestiones puede hacer un cliente sobre su cuenta y sus reservas." },
  administrator: { title: "Administrador", description: "Los administradores tienen acceso total a la operación del negocio." }
};

export function InfoTip({ children }: { children: string }): ReactElement {
  return <span className="settings-help" title={children} aria-label={children}>?</span>;
}

export function SettingsToggle({ checked, label, description, onChange, disabled = false }: { checked: boolean; label: string; description?: string; onChange: (checked: boolean) => void; disabled?: boolean }): ReactElement {
  return <label className={`settings-toggle ${disabled ? "disabled" : ""}`}>
    <span className="settings-switch">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
      <span className="settings-switch-track" aria-hidden="true"><span /></span>
    </span>
    <span className="settings-toggle-copy"><strong>{label}</strong>{description && <small>{description}</small>}</span>
  </label>;
}

export function SettingStepper({ value, min, max, step = 1, label, onChange }: { value: number; min: number; max: number; step?: number; label: string; onChange: (value: number) => void }): ReactElement {
  const update = (next: number) => {
    const normalized = Number.isFinite(next) ? Math.trunc(next) : min;
    onChange(Math.min(max, Math.max(min, normalized)));
  };
  return <div className="settings-stepper" aria-label={label}>
    <button type="button" aria-label={`Reducir ${label}`} onClick={() => update(value - step)} disabled={value <= min}>−</button>
    <input type="number" min={min} max={max} step={step} value={value} onChange={(event) => update(Number(event.target.value))} />
    <button type="button" aria-label={`Aumentar ${label}`} onClick={() => update(value + step)} disabled={value >= max}>+</button>
  </div>;
}

export function SettingsSaveBar({ saving, actionLabel = "Guardar", disabled = false, helper = "Los cambios se aplican al negocio", savingLabel = "Guardando…" }: { saving: boolean; actionLabel?: string; disabled?: boolean; helper?: string; savingLabel?: string }): ReactElement {
  return <div className="settings-save-bar"><span>{saving ? savingLabel : helper}</span><button className="button primary" type="submit" disabled={saving || disabled}>{saving ? savingLabel : actionLabel}</button></div>;
}

export function SettingsCard({ title, description, children, className = "" }: { title: string; description?: string; children: ReactNode; className?: string }): ReactElement {
  return <section className={`settings-card ${className}`}>
    <div className="settings-card-heading"><div><h3>{title}</h3>{description && <p>{description}</p>}</div></div>
    {children}
  </section>;
}
